/**
 * The one place the session token is read and written.
 *
 * Every module used to reach for `sessionStorage.getItem(STORAGE_KEYS.TOKEN)`
 * itself — 42 call sites across 26 files, including the API client, the fetch
 * helpers, the WebSocket handshakes and the auth hook. That is fine while the
 * token lives in sessionStorage and impossible to change while it does: moving
 * to an httpOnly cookie means the token stops being readable by page
 * JavaScript at all, and 42 scattered reads is 42 places to get that wrong.
 *
 * Behaviour here is deliberately identical to what those call sites did. This
 * change is a seam, not a policy change — the storage decision is made once,
 * in `readToken`, and the cookie migration edits this file rather than the
 * application.
 *
 * ## Why sessionStorage is not the end state
 *
 * Any script on the page can read it, so a single XSS hands over a valid
 * session. The credential-adjacent logging that made that materially worse was
 * purged on 22 Aug 2026 and `no-console` now warns, but the storage choice is
 * unchanged. Moving the token into an httpOnly, Secure, SameSite cookie takes
 * it out of JavaScript's reach entirely — and needs CSRF protection, which the
 * backend currently disables. That is the next step, and this file is where it
 * lands.
 */

import { STORAGE_KEYS } from '../utils/constants';

/**
 * The current session token, or null when signed out.
 *
 * Returns null rather than the empty string for a missing token, so callers can
 * use it directly in a truthiness check.
 */
export function readToken() {
  try {
    return sessionStorage.getItem(STORAGE_KEYS.TOKEN) || null;
  } catch {
    // Storage can throw in a sandboxed frame or with cookies blocked. A caller
    // that cannot read a token is signed out, not broken.
    return null;
  }
}

/** `Bearer <token>`, or null when signed out — the shape most call sites want. */
export function bearerToken() {
  const token = readToken();
  return token ? `Bearer ${token}` : null;
}

/**
 * An `Authorization` header, or an empty object when signed out.
 *
 * Spreadable into a headers literal without a conditional:
 *
 *     headers: { 'Content-Type': 'application/json', ...authHeader() }
 */
export function authHeader() {
  const bearer = bearerToken();
  return bearer ? { Authorization: bearer } : {};
}

/** Records the token that a sign-in returned. */
export function writeToken(token) {
  try {
    sessionStorage.setItem(STORAGE_KEYS.TOKEN, token);
  } catch {
    // Nothing useful to do: the sign-in succeeded and the next request will
    // simply be unauthenticated. Swallowed rather than thrown so it does not
    // turn a successful login into an error dialog.
  }
}

/** Forgets the token. Signing out must never throw. */
export function clearToken() {
  try {
    sessionStorage.removeItem(STORAGE_KEYS.TOKEN);
  } catch {
    /* see writeToken */
  }
}

/** Whether there is a session at all. */
export const hasSession = () => readToken() != null;
